
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Task } from '@task-utils/types';
import { DataTableComponent, Frame, GenericFilter, GenericFilterComponent, language, TableConfig, template, modal, ConfirmDialogComponent, color } from 'curvy';
import { locations_translations } from './locations.trans';
import { User } from '@task-modules/users';
import { CreateEditLocationDialog } from './create-edit-location.dialog';
import { TaskRoutes } from '@task-utils/routes';

@Component({
    selector: 'task-locations',
    template: `
        <div class="temp-add-button" *ngIf="can_create">
            <button (click)="add_location()"
                class="filled pill"
                color="primary"
            >
                <i icon="add_circle"></i>
                <span>{{ 'LOCATION.ADD_NEW' | trans }}</span>
            </button>
        </div>
        <knw-data-table #locationstable [config]="tableConfig"></knw-data-table>
    `,
    encapsulation: ViewEncapsulation.None,
    styles: [`
        task-locations {
            display: flex;
            flex-flow: column nowrap;
            overflow: hidden !important;
        }
    `]
})
export class LocationsComponent {
    @ViewChild('locationstable') table: DataTableComponent<Task.Location, DisplayLocation, 'locations', GenericFilterComponent>;

    can_create = false;

    constructor() {
        Frame.set({
            title: "NAVROUTE.LOCATIONS",
            visible: true,
            layout: "middle",
            size: "full"
        });
        language.load_translations(locations_translations);

        User.currentTypeIs(
            Task.USER_TYPE.SYSTEM_ADMIN,
            Task.USER_TYPE.ADMIN,
            Task.USER_TYPE.SUPPLIER_ADMIN,
            Task.USER_TYPE.SUPER_USER
        ).then(res=>{
            this.can_create = res;
        })
    }

    add_location() {
        modal.open(CreateEditLocationDialog, l => {
            if (l) this.table.getData();
        }, {
            cin: "",
            name: "",
            internal_tag: "",
            area: "",
            format: "",
            description: "",
            location_type_id: null,
            status_id: null,
            country: "",
            region: "",
            city: "",
            address: "",
            district: "",
            postal_code: "",
            contact_phone: "",
            email: "",
            location_users: [],
            gross_area: null,
            net_area: null,
            nfc: false,
            is_seasonal: false,
            tex_valve_count: null,
        } as Task.Location)
    };

    async search_companies(search: string) {
        let res = await TaskRoutes.companies.api_company_search(
            { search: [Task.COMPANY_TYPE.MAIN], attributes: ["#=:company_type_id"] },
            { page_no: 1, page_size: 10 }
        )

        return res.data;
    }

    format_company_name(comp: Task.Company) {
        if (!comp) return "";
        return comp.name;
    }

    tableConfig: TableConfig<Task.Location, DisplayLocation, 'locations', GenericFilterComponent> = {
        tableName: 'locations',
        getData: async (filter, paginator, sort_header) => {
            let parsed_filter = { search: [], attributes: [], sort: null };
            for (let key in filter) {
                if(key === 'company' && filter['company'] !== null) {
                    parsed_filter.attributes.push('#=:company_id')
                    parsed_filter.search.push(filter[key].company_id)
                }
                else {
                    parsed_filter.attributes.push(key);
                    parsed_filter.search.push(filter[key]);
                }
            }


            if (
                filter.length === 0 &&
                await User.currentTypeIs(
                    Task.USER_TYPE.SAFETY_MANAGER,
                    Task.USER_TYPE.REGIONAL_MAINTENANCE_MANAGER,
                    Task.USER_TYPE.SPECIALIST_1,
                    Task.USER_TYPE.SPECIALIST_2,
                    Task.USER_TYPE.SECTOR_ADMIN
                )
            ) {
                parsed_filter.attributes.push("location_user.user_id");
                parsed_filter.search.push((await User.currentUserPromise).user_id);
            }

            if (sort_header) {
                switch (sort_header.label) {
                    case "location":
                        sort_header.label = "cin"; break;
                }
                parsed_filter.sort = sort_header;
            }

            return await TaskRoutes.locations.api_location_search_visible(parsed_filter, paginator);
        },
        getHeader: (header) => {
            switch (header) {
                case 'status':
                    return { icon: "offline_bolt", label: "", sortable: false };

                case 'location':
                    return { icon: "place", label: "LOCATION.NAME", sortable: true };

                case 'address':
                    return { icon: "location_city", label: "LOCATION.ADDRESS", sortable: true };

                case 'contact':
                    return { icon: "contacts", label: "LOCATION.CONTACT", sortable: false };

                case 'details':
                    return { icon: "details", label: "DETAILS", sortable: false };

                case 'nfc':
                    return { icon: "contactless", label: "LOCGROUP.LOCATION_NCF", sortable: false };

            }
        },
        unwrapData: (location) => {
            let icon = LocationTypeIcon[location.location_type_id];
            let col = LocationTypeColor[location.location_type_id];
            let tooltip = language.translate(
                "LOCATION.TYPE." + Task.Location_Type[location.location_type_id]
            );

            let status_deleted = language.translate("STATUS.DELETED");
            let deleted = location.status_id === Task.STATUS.DELETED ? `<i icon="block" style="padding: 0 8px 0 0; color: rgba(var(--warn));" tooltip="${status_deleted}"></i>` : '';

            let cin_string = '';
            if (location.cin) {
                cin_string = '<span>#{{cin}}</span>';
            }

            let address: template.EscapedHTML;

            if (location.address) {
                address = template.transform (`<span>{{address}}</span>`, location);
            } else {
                address = template.prebuilt.icon("remove");
            }

            let contact: template.EscapedHTML;

            if (location.email && location.contact_phone) {
                contact = template.transform(`
                    <div class="flex-column align-center justify-center">
                        <a href="mailto:{{email}}">{{email}}</a>
                        <div class="flex-row align-center justify-center">
                            {{phone|icon}}
                            <div style="white-space: nowrap">{{contact_phone}}</div>
                        </div>
                    </div>
                `, location)
            } else if (location.email) {
                contact = template.transform(`
                    <div class="flex-column align-center justify-center">
                        <a href="mailto:{{email}}">{{email}}</a>
                    </div>
                `, location)
            } else if (location.contact_phone) {
                contact = template.transform(`
                    <div class="flex-column align-center justify-center">
                        <div class="flex-row align-center justify-center">
                            {{phone|icon}}
                            <div style="white-space: nowrap">{{contact_phone}}</div>
                        </div>
                    </div>
                `, location)
            } else {
                contact = template.prebuilt.icon("remove");
            }

            let nfc: template.EscapedHTML;
            if (location.nfc) {
                nfc = template.prebuilt.icon("check")
            } else {
                nfc = template.prebuilt.icon("");
            }

            return {
                status: template.transform(`
                    <div class="flex-column justify-center" style="padding: .25em 0;">
                        <div class="flex-row align-center justify-center">
                            <i style="color: {{icon_color}}">{{icon_name|icon}}</i></div>
                        <div style="font-size: 0.8em">
                            {{ status_name | trans }}
                        </div>
                    </div>
                    `, {
                    icon_name: Task.StatusIcons[location.status_id].icon,
                    icon_color: Task.StatusIcons[location.status_id].color,
                    status_name: language.translate('STATUS.' + Task.Status[location.status_id]),
                }),
                location: template.transform(`
                    <div class="flex-row align-center" style="width: 100%">
                        <i icon="${icon}"
                            tooltip-right="${tooltip}"
                            style="padding: 0 8px; color: ${col}"></i>
                            ${deleted}
                        <div class="flex-column flex-dynamic align-flex-start">
                            <b style="text-align: left">{{name}}</b>
                            ${cin_string}
                        </div>
                    </div>`, location),
                address: address,
                contact: contact,
                details: template.transform( `
                    <div class="flex-column" style="width: 100%; justify-content: flex-start">
                    <span>
                        ${(location.gross_area) ? 'bruto P: ' + location.gross_area +'m<sup>2</sup>' : ''}
                        </span>
                    <span>
                        ${(location.net_area) ? 'neto P: ' + location.net_area +'m<sup>2</sup>' : ''}
                        </span>
                    <span>
                        ${(location.tex_valve_count && location.tex_valve_count > 0) ? 'Broj tex ventila ' + location.tex_valve_count : ''}
                    </span>
                    </div>
                ` , {
                    gross_area: (location.gross_area) ? location.gross_area : '?',
                    net_area: (location.net_area) ? location.net_area : '?'
                    // net_area: this.testinjo()
                }),
                nfc: nfc
            }
        },
        showHeaderWhenEmpty: true,
        maxActions: window.innerWidth < 600 ? 1 : 3,

        // @todo: make translations not suck
        tableActions: [language.load_translations(locations_translations) as undefined || {
            label: language.translate('LOCATION.REPORT', language.load_translations(locations_translations)),
            icon: "download",
            onClick: async () => {
                TaskRoutes.reports.api_locations_data_report_get();
            },
            priority: 1,
            isVisible: () => User.is(
                Task.USER_TYPE.SYSTEM_ADMIN,
                Task.USER_TYPE.ADMIN
            )
        }],

        rowActions: [{
            label: language.translate('EDIT'),
            icon: "edit",
            onClick: (location) => {
                modal.open(CreateEditLocationDialog, l => {
                    if (l) this.table.getData();
                }, location);
            },
            priority: 1,
            isVisible: () => User.currentUser.user_type_id !== Task.USER_TYPE.SHELF_COORDINATOR

        }, {
            label : language.translate("COPY"),
            icon: "content_copy",
            onClick: (location) => {
                let c = JSON.parse(JSON.stringify(location));
                delete c.location_id;
                c.name = "";
                c.cin = "";

                modal.open(CreateEditLocationDialog, l => {
                    if (l) this.table.getData();
                }, c)
            },
            priority: 2,
            isVisible: () => User.currentUser.user_type_id !== Task.USER_TYPE.SHELF_COORDINATOR

        }, {
            label: language.translate('DELETE'),
            icon: "delete",
            color: "warn",
            onClick: async (loc) => {
                modal.open(ConfirmDialogComponent, async(succ)=>{
                    if (succ) {
                        await TaskRoutes.locations.api_location_delete(loc.location_id);
                        this.table.getData();
                    }
                }, {
                    title: language.translate("LOCATION.DELETE.TITLE"),
                    message: language.translate("LOCATION.DELETE.MSG", loc.name),
                    icon: "place",
                    iconColor: color.variable(color.Variable.warn)
                })
            },
            priority: 3,
            isVisible: () => [
                Task.USER_TYPE.ADMIN,
                Task.USER_TYPE.SYSTEM_ADMIN,
                Task.USER_TYPE.SUPER_USER].includes(User.currentUser.user_type_id)

        }],

        filterComponent: GenericFilterComponent,
        filterInput: async () => {
            let filters: GenericFilterComponent["filterInput"] = [
                GenericFilter.text("SEARCH", "*I:address+ +city+ +area+ +region+ +email+ +name+ +cin"),
                GenericFilter.text("LOCATION.INTERNAL.TAG", "*I:internal_tag"),
                GenericFilter.dropdown("LOCATION.TYPE", "location_type_id", [
                    { label: "LOCATION.TYPE.WAREHOUSE", value: Task.LOCATION_TYPE.WAREHOUSE },
                    { label: "LOCATION.TYPE.STORE", value: Task.LOCATION_TYPE.STORE },
                    { label: "LOCATION.TYPE.BUSINESS_FACILITY", value: Task.LOCATION_TYPE.BUSINESS_FACILITY },
                    { label: "LOCATION.TYPE.LEASED_LOCATION", value: Task.LOCATION_TYPE.LEASED_LOCATION },
                    { label: "LOCATION.TYPE.OFFICE", value: Task.LOCATION_TYPE.OFFICE}

                ]),
                GenericFilter.dropdown("LOCATION.STATUS", "status_id", [
                    { label: "STATUS.OPEN", value: Task.STATUS.OPEN },
                    { label: "STATUS.CLOSED", value: Task.STATUS.CLOSED },
                    { label: "STATUS.TEMPORARY_CLOSED_SHORT_TERM", value: Task.STATUS.TEMPORARY_CLOSED_SHORT_TERM },
                    { label: "STATUS.TEMPORARY_CLOSED_LONG_TERM", value: Task.STATUS.TEMPORARY_CLOSED_LONG_TERM },
                ]),
                GenericFilter.toggle("LOCATION.SEASONAL", "is_seasonal"),
                GenericFilter.toggle("LOCGROUP.LOCATION_NCF", "nfc"),
            ]

            if (await User.currentTypeIs(Task.USER_TYPE.SYSTEM_ADMIN)) {
               filters.push(GenericFilter.search("COMPANY", "company", this.search_companies, this.format_company_name));

            }

            return filters
        }
    }
}

const LocationTypeIcon = {
    [Task.LOCATION_TYPE.STORE]: "store",
    [Task.LOCATION_TYPE.WAREHOUSE]: "home_work",
    [Task.LOCATION_TYPE.OFFICE]: "domain",
    [Task.LOCATION_TYPE.SUPPLIER_LOCATION]: "service_toolbox",
    [Task.LOCATION_TYPE.OTHER]: "other_houses",
    [Task.LOCATION_TYPE.BUSINESS_FACILITY]: "business_center",
    [Task.LOCATION_TYPE.LEASED_LOCATION]: "local_convenience_store"
};

const LocationTypeColor = {
    [Task.LOCATION_TYPE.STORE]: "#55a",
    [Task.LOCATION_TYPE.WAREHOUSE]: "#49f",
    [Task.LOCATION_TYPE.OFFICE]: "#a55",
    [Task.LOCATION_TYPE.SUPPLIER_LOCATION]: Task.STATUS_COLORS.YELLOW,
    [Task.LOCATION_TYPE.OTHER]: Task.STATUS_COLORS.ORANGE,
    [Task.LOCATION_TYPE.BUSINESS_FACILITY]: "#d7c661",
    [Task.LOCATION_TYPE.LEASED_LOCATION]: "#47bf8e"
};

type DisplayLocation = Record<string, any>;
